import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { groupApi } from '@/domains/system-management/apis/group-management';
import type {
  BaseGroupRequest,
  GroupParams,
  UserParams,
  updateUserGroupParams,
} from '@/domains/system-management/types/group';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

export const useGroupManagement = (params: GroupParams) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Get groups list
  const useGroups = () => {
    return useQuery({
      queryKey: ['groups', params],
      queryFn: () => groupApi.getGroups(params),
    });
  };

  // Get group details
  const useGroup = (id: number | null) => {
    return useQuery({
      queryKey: ['group', id],
      queryFn: () => groupApi.getGroup(id),
      enabled: !!id,
    });
  };

  // Get user group
  const useUserGroup = (id: number | null) => {
    return useQuery({
      queryKey: ['userGroup', id],
      queryFn: () => groupApi.getUserGroup(id),
      enabled: !!id,
    });
  };

  // Create group
  const useCreateGroup = (onCreateSuccess?: (responseData: { group_code: number }) => void) => {
    return useMutation({
      mutationFn: (data: BaseGroupRequest) => groupApi.createGroup(data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['groups', params] });
            if (onCreateSuccess) onCreateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Delete group
  const useDeleteGroup = (onDeleteSuccess?: () => void) => {
    return useMutation({
      mutationFn: (id: number) => groupApi.deleteGroup(id),
      onSuccess: (_data, id) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > delete success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['userGroup', id] });
            queryClient.invalidateQueries({ queryKey: ['groups', params] });
            queryClient.invalidateQueries({ queryKey: ['navbarList'] });
            if (onDeleteSuccess) onDeleteSuccess();
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Update user group
  const useUpdateUserGroup = (onUpdateSuccess?: (id: number) => void) => {
    return useMutation({
      mutationFn: async ({ id, group, users }: updateUserGroupParams) => {
        const requests: Promise<unknown>[] = [];

        if (group && Object.keys(group).length > 0) {
          requests.push(groupApi.updateGroup(id, group));
        }
        if (users && Object.keys(users).length > 0) {
          requests.push(groupApi.updateUserGroup(id, users));
        }

        if (requests.length === 0) {
          throw new Error('No data provided');
        }

        await Promise.all(requests);
        return id;
      },
      onSuccess: (id) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > update success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['userGroup', id] });
            queryClient.invalidateQueries({ queryKey: ['group', id] });
            queryClient.invalidateQueries({ queryKey: ['groups', params] });
            if (onUpdateSuccess) onUpdateSuccess(id);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  return {
    useGroups,
    useGroup,
    useCreateGroup,
    useUserGroup,
    useDeleteGroup,
    useUpdateUserGroup,
  };
};

export const useUsers = (userParams: UserParams) => {
  return useQuery({
    queryKey: ['users', userParams],
    queryFn: () => groupApi.getUsers(userParams),
  });
};
